import React, { useEffect, useState } from "react";
import { Trophy, Plus, Pencil, Trash2, Loader2, X, Save, Globe, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import RichTextEditor from "../../components/admin/RichTextEditor";

const LANGS = ["tr", "en", "de"];
const STATUS = {
    draft: { label: "Taslak", color: "bg-slate-100 text-slate-600" },
    published: { label: "Yayında", color: "bg-data-green/10 text-data-green" },
    archived: { label: "Arşiv", color: "bg-amber-100 text-amber-700" },
};

export default function AdminStories() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/content/stories");
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const create = () => setEditing({
        person: { name: "", title: "", photo: "" },
        hotel: { name: "", location: "", star_level: 4 },
        video_url: "",
        featured_quote: { tr: "", en: "", de: "" },
        content: { tr: "<p></p>", en: "", de: "" },
        seo: { meta_title: "", meta_desc: "", slug: "" },
        status: "draft",
        sort_order: items.length + 1,
        isNew: true,
    });

    const editItem = async (id) => {
        try {
            const { data } = await api.get(`/admin/content/stories/${id}`);
            setEditing({ ...data, isNew: false });
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const save = async () => {
        if (!editing.hotel?.name || !editing.person?.name) {
            toast.error("Otel adı ve kişi adı zorunlu.");
            return;
        }
        try {
            const payload = { ...editing };
            delete payload.isNew;
            if (!editing.isNew) payload.id = editing.id;
            await api.post("/admin/content/stories", payload);
            toast.success("Kaydedildi.");
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Hikaye silinecek. Onaylıyor musunuz?")) return;
        try {
            await api.delete(`/admin/content/stories/${id}`);
            toast.success("Silindi.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div data-testid="admin-stories-page">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy inline-flex items-center gap-2">
                        <Trophy className="w-6 h-6 text-teal" /> Başarı Hikayeleri
                    </h1>
                    <p className="text-sm text-deep-blue/70 mt-1">Otel sahibi/GM/yatırımcı hikayeleri — TipTap içerik + YouTube embed.</p>
                </div>
                <button
                    onClick={create}
                    className="bg-gold hover:bg-gold-700 text-dark-navy text-sm font-bold px-4 py-2 rounded-md inline-flex items-center gap-2"
                    data-testid="stories-new"
                >
                    <Plus className="w-4 h-4" /> Yeni Hikaye
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-slate-100 overflow-hidden">
                {loading ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12 inline-flex items-center justify-center gap-2 w-full">
                        <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12">Henüz hikaye yok.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="stories-table">
                        <thead>
                            <tr className="bg-light-bg text-left text-xs uppercase tracking-wider text-deep-blue/70">
                                <th className="px-4 py-3">Otel / Kişi</th>
                                <th className="px-4 py-3">Lokasyon</th>
                                <th className="px-4 py-3">Yıldız</th>
                                <th className="px-4 py-3">Durum</th>
                                <th className="px-4 py-3 w-44">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((s) => {
                                const st = STATUS[s.status] || { label: s.status, color: "bg-slate-100 text-slate-600" };
                                return (
                                    <tr key={s.id} className="border-t border-slate-100">
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-dark-navy text-sm">{s.hotel?.name}</div>
                                            <div className="text-xs text-deep-blue/70">{s.person?.name} · {s.person?.title}</div>
                                        </td>
                                        <td className="px-4 py-3 text-xs">{s.hotel?.location}</td>
                                        <td className="px-4 py-3 font-data text-gold-700">{s.hotel?.star_level}★</td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${st.color}`}>{st.label}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1">
                                                {s.status === "published" && (
                                                    <a href={`/basari-hikayeleri/${s.seo?.slug || s.id}`} target="_blank" rel="noreferrer" title="Aç" className="p-1.5 rounded-md hover:bg-light-bg">
                                                        <ExternalLink className="w-4 h-4 text-teal-700" />
                                                    </a>
                                                )}
                                                <button onClick={() => editItem(s.id)} title="Düzenle" className="p-1.5 rounded-md hover:bg-light-bg" data-testid={`story-edit-${s.id}`}>
                                                    <Pencil className="w-4 h-4 text-deep-blue" />
                                                </button>
                                                <button onClick={() => remove(s.id)} title="Sil" className="p-1.5 rounded-md hover:bg-light-bg">
                                                    <Trash2 className="w-4 h-4 text-data-red" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && <StoryEditor story={editing} setStory={setEditing} onSave={save} onClose={() => setEditing(null)} />}
        </div>
    );
}

function StoryEditor({ story, setStory, onSave, onClose }) {
    const [activeLang, setActiveLang] = useState("tr");
    const u = (key, value) => setStory((p) => ({ ...p, [key]: value }));
    const upd = (group, key, value) => setStory((p) => ({ ...p, [group]: { ...(p[group] || {}), [key]: value } }));
    const updLang = (group, lang, value) =>
        setStory((p) => ({ ...p, [group]: { ...(p[group] || {}), [lang]: value } }));

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="story-editor">
            <div className="bg-white rounded-lg shadow-2xl border border-slate-200 max-w-3xl w-full my-8">
                <div className="flex items-center justify-between p-5 border-b border-slate-200">
                    <h2 className="text-base font-semibold text-dark-navy">{story.isNew ? "Yeni Hikaye" : `Düzenle: ${story.hotel?.name}`}</h2>
                    <button onClick={onClose} className="p-1.5 rounded-md hover:bg-light-bg">
                        <X className="w-4 h-4 text-deep-blue" />
                    </button>
                </div>
                <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
                    {/* Hotel + Person */}
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Otel Adı"><input value={story.hotel?.name || ""} onChange={(e) => upd("hotel", "name", e.target.value)} className="input" data-testid="story-hotel-name" /></Field>
                        <Field label="Lokasyon"><input value={story.hotel?.location || ""} onChange={(e) => upd("hotel", "location", e.target.value)} className="input" placeholder="Antalya / Belek" /></Field>
                        <Field label="Yıldız Seviyesi">
                            <select value={story.hotel?.star_level || 4} onChange={(e) => upd("hotel", "star_level", parseInt(e.target.value, 10))} className="input bg-white">
                                {[1, 2, 3, 4, 5].map((s) => <option key={s} value={s}>{s} ★</option>)}
                            </select>
                        </Field>
                        <Field label="Durum">
                            <select value={story.status || "draft"} onChange={(e) => u("status", e.target.value)} className="input bg-white" data-testid="story-status">
                                <option value="draft">Taslak</option>
                                <option value="published">Yayında</option>
                                <option value="archived">Arşiv</option>
                            </select>
                        </Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Kişi Adı"><input value={story.person?.name || ""} onChange={(e) => upd("person", "name", e.target.value)} className="input" data-testid="story-person-name" /></Field>
                        <Field label="Ünvan"><input value={story.person?.title || ""} onChange={(e) => upd("person", "title", e.target.value)} className="input" placeholder="Genel Müdür" /></Field>
                        <Field label="Foto URL"><input value={story.person?.photo || ""} onChange={(e) => upd("person", "photo", e.target.value)} className="input font-mono" /></Field>
                        <Field label="Video URL (YouTube)"><input value={story.video_url || ""} onChange={(e) => u("video_url", e.target.value)} className="input font-mono" placeholder="https://www.youtube.com/embed/..." /></Field>
                    </div>

                    {/* Lang tabs */}
                    <div className="flex items-center gap-1 border-b border-slate-200 pt-3">
                        {LANGS.map((l) => (
                            <button key={l} onClick={() => setActiveLang(l)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider ${activeLang === l ? "border-b-2 border-teal text-teal-700" : "text-deep-blue/70"}`} data-testid={`story-lang-${l}`}>
                                <Globe className="w-3 h-3 inline mr-1" /> {l}
                            </button>
                        ))}
                    </div>

                    <Field label={`Öne Çıkan Alıntı (${activeLang})`}>
                        <textarea
                            rows={2}
                            value={story.featured_quote?.[activeLang] || ""}
                            onChange={(e) => updLang("featured_quote", activeLang, e.target.value)}
                            className="input"
                        />
                    </Field>
                    <Field label={`Hikaye İçeriği (${activeLang})`}>
                        <RichTextEditor
                            key={`content-${activeLang}`}
                            value={story.content?.[activeLang] || ""}
                            onChange={(v) => updLang("content", activeLang, v)}
                            testId={`story-content-${activeLang}`}
                        />
                    </Field>

                    <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t border-slate-200">
                        <Field label="SEO Meta Title">
                            <input value={story.seo?.meta_title || ""} onChange={(e) => u("seo", { ...(story.seo || {}), meta_title: e.target.value })} className="input" />
                        </Field>
                        <Field label="SEO Meta Description">
                            <input value={story.seo?.meta_desc || ""} onChange={(e) => u("seo", { ...(story.seo || {}), meta_desc: e.target.value })} className="input" />
                        </Field>
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200">
                    <button onClick={onClose} className="px-4 py-2 rounded-md bg-light-bg text-deep-blue text-sm font-semibold">İptal</button>
                    <button onClick={onSave} className="px-4 py-2 rounded-md bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold inline-flex items-center gap-1" data-testid="story-save">
                        <Save className="w-4 h-4" /> Kaydet
                    </button>
                </div>
            </div>
            <style>{`.input { width: 100%; padding: 0.5rem 0.75rem; border-radius: 0.375rem; border: 1px solid rgb(203 213 225); font-size: 0.875rem; }`}</style>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">{label}</label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
